import { RefObject, useEffect, useState } from "react";

import { window } from "../window";

type UseIntersectionObserverOptions = {
  condition?: (entry: IntersectionObserverEntry) => boolean;
  freezeOnceVisible?: boolean;
  root?: RefObject<Element | null>;
  rootMargin?: string;
  threshold?: number | number[];
};

type UseIntersectionObserverReturn = {
  isOnViewPort: boolean;
};

export const useIntersectionObserver = (
  elementRef: RefObject<Element | null>,
  callback?: (entry: IntersectionObserverEntry) => void,
  options: UseIntersectionObserverOptions = {}
): UseIntersectionObserverReturn => {
  const {
    condition,
    freezeOnceVisible = false,
    root = null,
    rootMargin = "0%",
    threshold = 0,
  } = options;
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const isFrozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = ([entryDetails]: IntersectionObserverEntry[]) => {
    const { isIntersecting } = entryDetails ?? {};
    const shouldTriggerCallback = condition?.(entryDetails) ?? isIntersecting;

    if (shouldTriggerCallback) {
      callback?.(entryDetails);
    }
    setEntry(entryDetails);
  };

  useEffect(() => {
    const node = elementRef?.current;
    const rootNode = root?.current;
    const isObserverSupported = !!window.IntersectionObserver;
    let observer: IntersectionObserver | undefined;

    if (isObserverSupported && node && !isFrozen) {
      observer = new window.IntersectionObserver(updateEntry, {
        root: rootNode,
        rootMargin,
        threshold,
      });
      observer.observe(node);
    }

    return () => observer?.disconnect();
  }, [elementRef, isFrozen, root, rootMargin, threshold]);

  return { isOnViewPort: !!entry?.isIntersecting };
};
